import { BackendError, Service } from '@genial/services';
import type { FixedIncomePositionEntity } from '@genial/services';
import {
  FixedIncomeOfferBaseEntity,
  FixedIncomeRequestsFixedIncomeOfferCategoriesResponse,
  MetaResponse,
} from '@genial/services/dist/models';
import { selector, selectorFamily } from 'recoil';

import { blankMetaResponse, fakeMetaResponse } from '../../../utils/blankMetaResponse';
import { transformSortItem } from '../../../utils/transformSortItem';
import { customerItemState } from '../../customer/atom';
import { advisorIdToApisSelector, partnerIdToApisSelector } from '../../master/selector';
import {
  fixedIncomeOffersByFilterParamsState,
  fixedIncomeOffersBySearchTermParamsState,
  fixedIncomeOffersAllState,
  fixedIncomeOffersBySearchTermState,
  fixedIncomeCategoriesFilterState,
  fixedIncomeOffersOperationTypeState,
  fixedIncomeRescueOffersAllState,
  fixedIncomeRescueOffersBySearchTermParamsState,
  fixedIncomeRescueOffersBySearchTermState,
} from '../atom';
import { screenName } from '../constants';
import {
  normalizeFixedIncomeFilters,
  normalizeFixedIncomeFiltersFromMeta,
  normalizeFixedIncomeOffers,
  normalizeFixedIncomeSellOffers,
} from '../normalizer';
import {
  DynamicAssetsQueryCustomerParams,
  DynamicAssetsQueryParams,
  DynamicCustomFiltersProps,
  DynamicMarketStatusProps,
  FixedIncomeOfferEntityNormalized,
  FixedIncomeOffersByFilterParams,
  FixedIncomeOffersBySearchTerm,
  FixedIncomeOfferSellEntityNormalized,
  type FixedIncomeRescueOffersBySearchTerm,
} from '../types';

type FixedIncomeOffersQueryData = {
  data: FixedIncomeRequestsFixedIncomeOfferCategoriesResponse[];
};

export const fixedIncomeOffersByFilterParams = selector({
  key: 'fixedIncomeOffersByFilterParams',
  set: ({ set, get }, newValue) => {
    const currentFixedIncomeOffersByFilter = get(fixedIncomeOffersByFilterParamsState);
    const value = newValue as Partial<FixedIncomeOffersByFilterParams>;

    const fixedIncomeOffersByFilter = {
      ...currentFixedIncomeOffersByFilter,
      ...value,
    };

    return set(fixedIncomeOffersByFilterParamsState, fixedIncomeOffersByFilter);
  },
  get: ({ get }) => {
    const item = get(fixedIncomeOffersByFilterParamsState);
    return item;
  },
});

export const fixedIncomeOffersBySearchTermParams = selector({
  key: 'fixedIncomeOffersBySearchTermParams',
  set: ({ set, get }, newValue) => {
    const currentFixedIncomeOffersBySearchTerm = get(fixedIncomeOffersBySearchTermParamsState);
    const value = newValue as Partial<FixedIncomeOffersBySearchTerm>;

    const fixedIncomeOffersBySearchTerm = {
      ...currentFixedIncomeOffersBySearchTerm,
      ...value,
    };

    return set(fixedIncomeOffersBySearchTermParamsState, fixedIncomeOffersBySearchTerm);
  },
  get: ({ get }) => {
    const item = get(fixedIncomeOffersBySearchTermParamsState);
    return item;
  },
});

export const fixedIncomeOffersQuery = selector({
  key: 'NewApplication/fixedIncomeOffersQuery',
  get: async ({ get }) => {
    try {
      const fixedIncomeParams = get(fixedIncomeOffersByFilterParamsState);
      const advisorSelected = get(advisorIdToApisSelector);
      const partnerSelected = get(partnerIdToApisSelector);

      const { data, meta } = await Service.FixedIncome.V1.getOffersFilter({
        ...fixedIncomeParams,
        screenName,
        advisorId: advisorSelected,
        partnerId: partnerSelected,
      });

      const normalizedData = data?.map((item) => item.offer) || [];

      return { data: normalizedData, meta };
    } catch (error) {
      const backendError = error as BackendError;

      if (backendError.statusCode === 404) {
        return { data: [], meta: blankMetaResponse() };
      }

      throw error;
    }
  },
});

export const fixedIncomeOffersBySearchTermQuery = selector({
  key: 'NewApplication/fixedIncomeOffersBySearchTermQuery',
  get: async ({ get }) => {
    const fixedIncomeParams = get(fixedIncomeOffersBySearchTermParamsState);
    const advisorSelected = get(advisorIdToApisSelector);
    const partnerSelected = get(partnerIdToApisSelector);

    if (fixedIncomeParams.skipEndpointCall) {
      return;
    }

    const { data, meta } = await Service.FixedIncome.V1.getOffersFilter({
      ...fixedIncomeParams,
      screenName,
      advisorId: advisorSelected,
      partnerId: partnerSelected,
    });

    const normalizedData = data?.map((item) => item.offer) || [];

    return { data: normalizedData, meta };
  },
});

export const updateFixedIncomeOffersAllSelector = selector({
  key: 'updateFixedIncomeOffersAllSelector',
  set: ({ set }, newValue) => {
    const data = newValue as FixedIncomeOfferBaseEntity[];
    return set(fixedIncomeOffersAllState, data);
  },
  get: ({ get }) => {
    const item = get(fixedIncomeOffersAllState);
    return item;
  },
});

export const updateFixedIncomeOffersBySearchTermSelector = selector({
  key: 'updateFixedIncomeOffersBySearchTermSelector',
  set: ({ set }, newValue) => {
    const data = newValue as FixedIncomeOfferBaseEntity[];
    return set(fixedIncomeOffersBySearchTermState, data);
  },
  get: ({ get }) => {
    const item = get(fixedIncomeOffersBySearchTermState);
    return item;
  },
});

export const fixedIncomeCategoriesQuery = selectorFamily<
  FixedIncomeOffersQueryData | null | undefined,
  { skipEndpointCall?: boolean }
>({
  key: 'NewApplication/fixedIncomeCategoriesQuery',
  get: ({ skipEndpointCall }) => {
    return async ({ get }) => {
      try {
        if (skipEndpointCall) {
          const fixedIncomeCategoriesFilter = get(fixedIncomeCategoriesFilterState);
          return fixedIncomeCategoriesFilter;
        }
        const { data } = await Service.FixedIncome.V1.getOffersCategories({
          screenName,
        });

        const normalizedData = data?.map((item) => item) || [];
        return { data: normalizedData };
      } catch (error) {
        const backendError = error as BackendError;

        if (backendError.statusCode === 404) {
          return { data: [] };
        }

        throw error;
      }
    };
  },
});

export const fixedIncomeMarketStatusQuery = selector<DynamicMarketStatusProps>({
  key: 'NewApplication/fixedIncomeMarketStatusQuery',
  get: async () => {
    const { data } = await Service.FixedIncome.V1.getMarketStatus({
      screenName,
    });

    return {
      isOpen: !!(data.markets && data.markets.length > 1 && data.markets[1]?.isOpened),
      message: data.message || '',
      originalData: data,
    };
  },
});

export const fixedIncomeOffersQueryUpdated = selectorFamily<
  {
    data: FixedIncomeOfferEntityNormalized[];
    meta: MetaResponse;
    filters?: DynamicCustomFiltersProps['filters'];
  },
  DynamicAssetsQueryParams
>({
  key: 'NewApplication/fixedIncomeOffersQueryUpdated',
  get: ({ filtersToApply, accountId, multipleAccountsIds, searchTerm, page, orderBy }) => {
    return async ({ get }) => {
      const accountIds = multipleAccountsIds || (accountId ? [accountId] : []);

      try {
        const advisorSelected = get(advisorIdToApisSelector);
        const partnerSelected = get(partnerIdToApisSelector);
        const sortItems = transformSortItem(orderBy);

        const { data, meta } = await Service.FixedIncome.V1.getOffersFilter({
          ...filtersToApply,
          ...(searchTerm ? { searchTerm } : {}),
          sortItems: sortItems && [sortItems],
          page,
          limit: 12,
          screenName,
          advisorId: advisorSelected,
          partnerId: partnerSelected,
          accountIds,
        });

        const normalizedData = normalizeFixedIncomeOffers(data);
        const normalizedFilters = normalizeFixedIncomeFiltersFromMeta(meta);

        return { data: normalizedData, meta, filters: normalizedFilters };
      } catch (error) {
        const backendError = error as BackendError;

        if (backendError.statusCode === 404) {
          return { data: [], meta: blankMetaResponse(), filters: [] };
        }

        throw error;
      }
    };
  },
});

export const fixedIncomeSellOffersQueryUpdated = selectorFamily<
  { data: FixedIncomeOfferSellEntityNormalized[]; meta: MetaResponse },
  DynamicAssetsQueryParams
>({
  key: 'NewApplication/fixedIncomeSellOffersQueryUpdated',
  get: ({ searchTerm, accountNumber }) => {
    return async () => {
      try {
        const serviceToCall = searchTerm
          ? Service.FixedIncome.V1.getRedemptionsByAccountNumberSearchTerm
          : Service.FixedIncome.V1.getAllRedemptions;

        const { data } = await serviceToCall({
          searchTerm: searchTerm || '',
          screenName,
          accountNumber,
        });

        const normalizedData = normalizeFixedIncomeSellOffers(
          data?.portfolio.fixedIncomeProductPosition || []
        );

        return { data: normalizedData, meta: fakeMetaResponse(normalizedData.length) };
      } catch (error) {
        const backendError = error as BackendError;

        if (backendError.statusCode === 404) {
          return { data: [], meta: blankMetaResponse() };
        }

        throw error;
      }
    };
  },
});

export const fixedIncomeCustomFiltersQuery = selectorFamily<
  DynamicCustomFiltersProps,
  DynamicAssetsQueryCustomerParams
>({
  key: 'NewApplication/fixedIncomeCustomFiltersQuery',
  get: () => {
    return async () => {
      const { data } = await Service.FixedIncome.V1.getOffersCategories({
        screenName,
      });

      const normalizedData = normalizeFixedIncomeFilters(data);

      return normalizedData;
    };
  },
});

export const updateFixedIncomeCategoriesOptionsFilterSelector = selector({
  key: 'updateFixedIncomeCategoriesOptionsFilterSelector',
  set: ({ set }, newValue) => {
    const value = { data: newValue } as {
      data: FixedIncomeRequestsFixedIncomeOfferCategoriesResponse[];
    };
    return set(fixedIncomeCategoriesFilterState, value);
  },
  get: ({ get }) => {
    const item = get(fixedIncomeCategoriesFilterState);
    return item;
  },
});

export const updateFixedIncomeOffersOperationTypeSelector = selector({
  key: 'updateFixedIncomeOffersOperationTypeSelector',
  get: ({ get }) => {
    const item = get(fixedIncomeOffersOperationTypeState);
    return item;
  },
  set: ({ set }, newValue) => {
    const data = newValue;
    return set(fixedIncomeOffersOperationTypeState, data);
  },
});

export const fixedIncomeRescueOffersQuery = selector({
  key: 'NewApplication/fixedIncomeRescueOffersQuery',
  get: async ({ get }) => {
    try {
      const { accountNumber } = get(customerItemState).current;

      const { data } = await Service.FixedIncome.V1.getAllRedemptions({
        screenName,
        accountNumber,
      });

      const normalizedData = data?.portfolio.fixedIncomeProductPosition || [];

      return { data: normalizedData };
    } catch (error) {
      const backendError = error as BackendError;

      if (backendError.statusCode === 404) {
        return { data: [] };
      }

      throw error;
    }
  },
});

export const fixedIncomeRescueOffersBySearchTermQuery = selector({
  key: 'NewApplication/fixedIncomeRescueOffersBySearchTermQuery',
  get: async ({ get }) => {
    const fixedIncomeRescueBySearchTermParams = get(fixedIncomeRescueOffersBySearchTermParamsState);
    const { accountNumber } = get(customerItemState).current;

    if (fixedIncomeRescueBySearchTermParams.skipEndpointCall) {
      return;
    }

    const { data } = await Service.FixedIncome.V1.getRedemptionsByAccountNumberSearchTerm({
      ...fixedIncomeRescueBySearchTermParams,
      screenName,
      accountNumber,
    });

    const normalizedData = data?.portfolio.fixedIncomeProductPosition || [];

    return { data: normalizedData };
  },
});

export const updateFixedIncomeRescueOffersAllSelector = selector({
  key: 'updateFixedIncomeRescueOffersAllSelector',
  set: ({ set }, newValue) => {
    const data = newValue as FixedIncomePositionEntity[];
    return set(fixedIncomeRescueOffersAllState, data);
  },
  get: ({ get }) => {
    const item = get(fixedIncomeRescueOffersAllState);
    return item;
  },
});

export const updateFixedIncomeRescueOffersBySearchTermParams = selector({
  key: 'fixedIncomeRescueOffersBySearchTermParams',
  set: ({ set, get }, newValue) => {
    const currentFixedIncomeOffersRescueBySearchTerm = get(
      fixedIncomeRescueOffersBySearchTermParamsState
    );
    const value = newValue as Partial<FixedIncomeRescueOffersBySearchTerm>;
    const fixedIncomeOffersBySearchTerm = {
      ...currentFixedIncomeOffersRescueBySearchTerm,
      ...value,
    };

    return set(fixedIncomeRescueOffersBySearchTermParamsState, fixedIncomeOffersBySearchTerm);
  },
  get: ({ get }) => {
    const item = get(fixedIncomeRescueOffersBySearchTermParamsState);
    return item;
  },
});

export const updateFixedIncomeRescueOffersBySearchTermSelector = selector({
  key: 'updateFixedIncomeRescueOffersBySearchTermSelector',
  set: ({ set }, newValue) => {
    const data = newValue as FixedIncomePositionEntity[];
    return set(fixedIncomeRescueOffersBySearchTermState, data);
  },
  get: ({ get }) => {
    const item = get(fixedIncomeRescueOffersBySearchTermState);
    return item;
  },
});
